import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Bell,
  CheckCircle2,
  Trash2,
  Filter,
  Clock,
  AlertCircle,
  Info,
  Package,
  DollarSign,
} from 'lucide-react';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  priority?: string;
  recipientType?: string;
  recipientId?: string;
  shipmentId?: number;
  readAt?: string | null;
  isRead?: boolean;
  channel: string;
  status: string;
  sentAt?: string | null;
  createdAt: string;
}

const T = {
  title: '\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a',
  subtitle: '\u0625\u062f\u0627\u0631\u0629 \u0648\u0639\u0631\u0636 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a',
  totalNotifications: '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a',
  unreadNotifications: '\u063a\u064a\u0631 \u0645\u0642\u0631\u0648\u0621\u0629',
  readNotifications: '\u0645\u0642\u0631\u0648\u0621\u0629',
  todayNotifications: '\u0627\u0644\u064a\u0648\u0645',
  loading: '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...',
  noNotifications: '\u0644\u0627 \u062a\u0648\u062c\u062f \u0625\u0634\u0639\u0627\u0631\u0627\u062a',
  markAsRead: '\u062a\u0639\u0644\u064a\u0645 \u0643\u0645\u0642\u0631\u0648\u0621',
  markAllAsRead: '\u062a\u0639\u0644\u064a\u0645 \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621',
  delete: '\u062d\u0630\u0641',
  deleteAll: '\u062d\u0630\u0641 \u0627\u0644\u0643\u0644',
  filter: '\u0641\u0644\u062a\u0631\u0629',
  allTypes: '\u062c\u0645\u064a\u0639 \u0627\u0644\u0623\u0646\u0648\u0627\u0639',
  allStatus: '\u062c\u0645\u064a\u0639 \u0627\u0644\u062d\u0627\u0644\u0627\u062a',
  unread: '\u063a\u064a\u0631 \u0645\u0642\u0631\u0648\u0621',
  read: '\u0645\u0642\u0631\u0648\u0621',
  typeSystem: '\u0646\u0638\u0627\u0645',
  typeShipment: '\u0634\u062d\u0646\u0629',
  typePayment: '\u062f\u0641\u0639',
  typeUser: '\u0645\u0633\u062a\u062e\u062f\u0645',
  typeAlert: '\u062a\u0646\u0628\u064a\u0647',
  confirmDelete: '\u0647\u0644 \u0623\u0646\u062a \u0645\u062a\u0623\u0643\u062f \u0645\u0646 \u062d\u0630\u0641 \u0647\u0630\u0627 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u061f',
  confirmDeleteAll: '\u0647\u0644 \u0623\u0646\u062a \u0645\u062a\u0623\u0643\u062f \u0645\u0646 \u062d\u0630\u0641 \u062c\u0645\u064a\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a\u061f',
  deleted: '\u062a\u0645 \u062d\u0630\u0641 \u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u0628\u0646\u062c\u0627\u062d',
  allDeleted: '\u062a\u0645 \u062d\u0630\u0641 \u062c\u0645\u064a\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a \u0628\u0646\u062c\u0627\u062d',
  markedAsRead: '\u062a\u0645 \u062a\u0639\u0644\u064a\u0645 \u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u0643\u0645\u0642\u0631\u0648\u0621',
  allMarkedAsRead: '\u062a\u0645 \u062a\u0639\u0644\u064a\u0645 \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621',
  error: '\u062d\u062f\u062b \u062e\u0637\u0623',
  minutes: '\u062f\u0642\u064a\u0642\u0629',
  hours: '\u0633\u0627\u0639\u0629',
  days: '\u064a\u0648\u0645',
  ago: '\u0645\u0636\u062a',
};

export default function NotificationsPage() {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
  }, [user?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);

      const userId = user?.id?.toString() || '';
      if (!userId) {
        setNotifications([]);
        return;
      }

      const res = await api.get(
        `/api/notifications?pageSize=100&recipientType=user&recipientId=${userId}`
      );

      if (res.data?.success) {
        const data = res.data?.data ?? res.data;

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];

        setNotifications(items);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const isNotificationRead = (notification: Notification) => {
    if (typeof notification.isRead === 'boolean') {
      return notification.isRead;
    }

    return Boolean(notification.readAt);
  };

  const markAsRead = async (id: number) => {
    try {
      await api.put(`/api/notifications/${id}/read`);
      await loadNotifications();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await api.put('/api/notifications/mark-all-read');

      if (res.data?.success) {
        await loadNotifications();
        alert(T.allMarkedAsRead);
      }
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      alert(err.response?.data?.message || T.error);
    }
  };

  const deleteNotification = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) {
      return;
    }

    try {
      await api.delete(`/api/notifications/${id}`);
      await loadNotifications();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm(T.confirmDeleteAll)) {
      return;
    }

    try {
      const res = await api.delete('/api/notifications/all');

      if (res.data?.success) {
        await loadNotifications();
        alert(T.allDeleted);
      }
    } catch (err: any) {
      console.error('Error deleting all:', err);
      alert(err.response?.data?.message || T.error);
    }
  };

  const filtered = notifications.filter((notification) => {
    const read = isNotificationRead(notification);

    const matchType =
      typeFilter === 'all' || notification.type === typeFilter;

    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'read' && read) ||
      (statusFilter === 'unread' && !read);

    return matchType && matchStatus;
  });

  const unreadCount = notifications.filter(
    (notification) => !isNotificationRead(notification)
  ).length;

  const readCount = notifications.filter(
    (notification) => isNotificationRead(notification)
  ).length;

  const todayCount = notifications.filter((notification) => {
    const today = new Date().toDateString();

    return new Date(notification.createdAt).toDateString() === today;
  }).length;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Shipment':
        return Package;
      case 'Payment':
        return DollarSign;
      case 'User':
        return Info;
      case 'Alert':
        return AlertCircle;
      default:
        return Bell;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Shipment':
        return 'bg-blue-100 text-blue-600';
      case 'Payment':
        return 'bg-green-100 text-green-600';
      case 'User':
        return 'bg-purple-100 text-purple-600';
      case 'Alert':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'System':
        return T.typeSystem;
      case 'Shipment':
        return T.typeShipment;
      case 'Payment':
        return T.typePayment;
      case 'User':
        return T.typeUser;
      case 'Alert':
        return T.typeAlert;
      default:
        return type;
    }
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) {
      return 'just now';
    }

    if (minutes < 60) {
      return `${minutes} ${T.minutes} ${T.ago}`;
    }

    if (hours < 24) {
      return `${hours} ${T.hours} ${T.ago}`;
    }

    return `${days} ${T.days} ${T.ago}`;
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <Bell className="w-6 h-6 text-white" />
          </div>

          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">
              {T.subtitle}
            </p>
          </div>
        </h1>

        <div className="flex gap-2">
          <Button
            onClick={markAllAsRead}
            variant="outline"
            className="bg-green-50 text-green-700 hover:bg-green-100"
          >
            <CheckCircle2 className="w-4 h-4 ml-2" />
            {T.markAllAsRead}
          </Button>

          <Button
            onClick={deleteAll}
            variant="outline"
            className="bg-red-50 text-red-700 hover:bg-red-100"
          >
            <Trash2 className="w-4 h-4 ml-2" />
            {T.deleteAll}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {T.totalNotifications}
            </p>

            <p className="text-2xl font-bold text-gray-900">
              {notifications.length}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {T.unreadNotifications}
            </p>

            <p className="text-2xl font-bold text-red-700">
              {unreadCount}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {T.readNotifications}
            </p>

            <p className="text-2xl font-bold text-green-700">
              {readCount}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {T.todayNotifications}
            </p>

            <p className="text-2xl font-bold text-blue-700">
              {todayCount}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />

              <span className="text-sm font-medium text-gray-700">
                {T.filter}:
              </span>
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
            >
              <option value="all">{T.allTypes}</option>
              <option value="System">{T.typeSystem}</option>
              <option value="Shipment">{T.typeShipment}</option>
              <option value="Payment">{T.typePayment}</option>
              <option value="User">{T.typeUser}</option>
              <option value="Alert">{T.typeAlert}</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
            >
              <option value="all">{T.allStatus}</option>
              <option value="unread">{T.unread}</option>
              <option value="read">{T.read}</option>
            </select>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto" />

              <p className="text-gray-500 mt-4">
                {T.loading}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {T.noNotifications}
              </h3>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((notification) => {
                const TypeIcon = getTypeIcon(notification.type);
                const isRead = isNotificationRead(notification);

                return (
                  <div
                    key={notification.id}
                    className={`p-4 rounded-xl border transition ${
                      isRead
                        ? 'bg-white border-gray-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getTypeColor(
                          notification.type
                        )}`}
                      >
                        <TypeIcon className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h3
                              className={`font-semibold ${
                                isRead
                                  ? 'text-gray-700'
                                  : 'text-gray-900'
                              }`}
                            >
                              {notification.title}
                            </h3>

                            <p className="text-sm text-gray-600 mt-1">
                              {notification.message}
                            </p>

                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-gray-500">
                                {getTimeAgo(notification.createdAt)}
                              </span>

                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeColor(
                                  notification.type
                                )}`}
                              >
                                {getTypeLabel(notification.type)}
                              </span>

                              {!isRead && (
                                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                  {T.unread}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            {!isRead && (
                              <button
                                onClick={() =>
                                  markAsRead(notification.id)
                                }
                                className="p-2 hover:bg-green-50 rounded-lg text-gray-500 hover:text-green-600 transition"
                                title={T.markAsRead}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                            )}

                            <button
                              onClick={() =>
                                deleteNotification(notification.id)
                              }
                              className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition"
                              title={T.delete}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
